import { useCallback } from "react";
import { useCachedQuery } from "./useCachedQuery";
import { fetchTenantMetrics, type TenantMetrics } from "./useAdminStatus";
import { instanceMetricsResource } from "../lib/adminQueries";
import { rangeToWindowHours, type InstanceRange } from "../lib/instanceDetailRange";

export type InstanceMetricsResult = {
  status: "loading" | "ready" | "error";
  metrics: TenantMetrics | null;
  error: string | null;
  isFetching: boolean;
};

export const useInstanceMetrics = (tenantId: string, range: InstanceRange): InstanceMetricsResult => {
  const hours = rangeToWindowHours(range);
  const fetcher = useCallback(
    (signal: AbortSignal): Promise<TenantMetrics> => fetchTenantMetrics(tenantId, hours, signal),
    [tenantId, hours],
  );
  const { status, data, error, isFetching } = useCachedQuery<TenantMetrics>({
    // Cache only the default 24h window; longer windows are network-only so a
    // 30d payload never paints as the 24h chart on a cold cache.
    cacheResource: range === "24h" ? instanceMetricsResource(tenantId) : null,
    queryKey: `/api/admin/claws/${tenantId}/metrics?hours=${hours}`,
    fetcher,
  });
  return { status, metrics: data, error, isFetching };
};
